import { webmToOgg } from './webmToOgg';

// Recording a voice note (Gary, 15 Oct). iPhones record AAC in an MP4 file, which WhatsApp
// takes as is; Android's Chrome records Opus in WebM, which WhatsApp refuses, so it is
// repackaged as Ogg Opus (webmToOgg) before sending. Nothing is re-encoded.

export interface Recording {
  /** Stops and returns the file to send, or null if nothing usable was recorded. */
  stop: () => Promise<File | null>;
  cancel: () => void;
}

// Opus first, MP4 last (16 Oct): Android's Chrome now also offers audio/mp4, but its MP4
// holds Opus, which WhatsApp refuses ("Media upload error", 131053, Barry's first voice
// note). iPhones offer only audio/mp4, with AAC, which WhatsApp takes.
const PREFERRED = ['audio/ogg;codecs=opus', 'audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];

export async function startRecording(): Promise<Recording> {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const mimeType = PREFERRED.find((m) => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m));
  const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
  const chunks: Blob[] = [];
  rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
  rec.start();
  const release = () => stream.getTracks().forEach((t) => t.stop());

  return {
    cancel: () => { try { rec.stop(); } catch { /* already stopped */ } release(); },
    stop: () => new Promise<File | null>((resolve) => {
      rec.onstop = async () => {
        release();
        const type = (rec.mimeType || mimeType || '').toLowerCase();
        const blob = new Blob(chunks, { type });
        if (!blob.size) { resolve(null); return; }
        if (type.includes('webm')) {
          const ogg = webmToOgg(new Uint8Array(await blob.arrayBuffer()));
          resolve(ogg ? new File([ogg.slice().buffer as ArrayBuffer], 'voice-note.ogg', { type: 'audio/ogg' }) : null);
        } else if (type.includes('ogg')) {
          resolve(new File([blob], 'voice-note.ogg', { type: 'audio/ogg' }));
        } else {
          resolve(new File([blob], 'voice-note.m4a', { type: 'audio/mp4' }));
        }
      };
      try { rec.stop(); } catch { release(); resolve(null); }
    }),
  };
}
